import { Button, FlexCenter } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import type { FC } from "react";
import { useEffect } from "react";
import { Dialog, Modal as RAModal, ModalOverlay } from "react-aria-components";
import { useLocation } from "react-router-dom";

import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { useBloc } from "src/state/state";
import UserCubit from "src/state/UserCubit/UserCubit";
import Translate from "src/ui/components/Translate/Translate";
import WithBackground from "src/ui/components/WithBackground/WithBackground";
import { cmsImageUrl } from "src/lib/cmsImageUrl";
import ConversationThreadCubit from "src/ui/components/LeapChat/state/ConversationThreadCubit";
import RequestPushPermissionsCubit from "./RequestPushPermissionsCubit";

const avatar01 = cmsImageUrl(
  "e91edfdf1baf3a52b8ba13448ee605992314fc97-112x112.jpg"
);
const avatar02 = cmsImageUrl(
  "235b659fcdf4120000de61e6e9b9a9f7be7f5b02-112x112.jpg"
);
const avatar03 = cmsImageUrl(
  "46bbacbb3399885bae2a6440be9582e401e780e1-112x112.jpg"
);
const avatar04 = cmsImageUrl(
  "a1cbe6f864cdb70c63b2e48aad93e2a6ab5af551-112x112.jpg"
);

const avatars = [avatar01, avatar02, avatar03, avatar04].sort(
  () => 0.5 - Math.random()
);

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(2em);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Wraps in react-aria's overlay stack so this full-page prompt inerts any
// open Modal underneath (and isn't itself inerted when stacked above one).
const FullPageOverlay = styled(ModalOverlay)`
  position: fixed;
  inset: 0;
  z-index: 90001;
  animation: ${slideUp} 0.4s ease forwards;
`;

const Panel = styled(RAModal)`
  display: contents;
`;

const StyledDialog = styled(Dialog)`
  display: contents;
  outline: none;
`;

const RequestPushPermissions: FC = () => {
  const [{ show }, { evaluate, handlePromptNow, handleDismiss, init }] =
    useBlocNext(RequestPushPermissionsCubit);
  const [{ suspendUi }] = useBloc(UserCubit);
  const [pushState, { checkPermissions, isAvailable }] = useBlocNext(
    PushNotificationsBloc
  );
  const [{ escalationOccurred }] = useBlocNext(AppViewCubit);
  const location = useLocation();
  const conversationId = location.pathname.match(
    /\/conversation\/([^/]+)/
  )?.[1];
  const [threadState] = useBlocNext(ConversationThreadCubit, {
    id: conversationId
  });

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!suspendUi) {
      void checkPermissions();
    }
  }, [suspendUi]);

  useEffect(() => {
    evaluate();
  }, [
    pushState,
    threadState.messages,
    threadState.isInitializing,
    escalationOccurred,
    isAvailable
  ]);

  if (!isAvailable || !show) {
    return null;
  }

  return (
    <FullPageOverlay isOpen isDismissable={false} isKeyboardDismissDisabled>
      <Panel>
        <StyledDialog aria-label="Push notifications">
          <WithBackground variant="animate-primary">
            <FlexCenter
              style={{ padding: "var(--space-lg)", textAlign: "center" }}
            >
              <nine-avatar-list>
                {avatars.map((img) => (
                  <nine-avatar key={img} img={img}></nine-avatar>
                ))}
              </nine-avatar-list>
              <h4>
                <Translate msg="notification.careTeamReplies" />
              </h4>
              <nine-spacer s="md"></nine-spacer>
              <Button theme="charcoal" center onPress={handlePromptNow}>
                <Translate msg="notification.action" />
              </Button>
              <nine-spacer s="sm"></nine-spacer>
              <Button
                theme="transparent"
                hideArrow
                center
                onPress={handleDismiss}
              >
                <Translate msg="notNow" />
              </Button>
            </FlexCenter>
          </WithBackground>
        </StyledDialog>
      </Panel>
    </FullPageOverlay>
  );
};

export default RequestPushPermissions;
